/**
 * DashboardEditor — wspólny edytor układu pulpitu (Faza 23, Etap 2).
 *
 * Wyciągnięty z `TenantDashboard` (pulpit osobisty), używany też przez edytor DOMYŚLNEGO pulpitu
 * tenanta. Cała maszyneria edycji jest wspólna: tryb edycji na kopii roboczej (`draft` — Anuluj jest
 * darmowe), picker kafelków, modal konfiguracji, potwierdzenie usunięcia, drag/resize, zapis.
 * Parametryzowany:
 *  - `baseLayout` — układ do wyświetlenia/edycji (u pulpitu osobistego = resolver osobisty→default→
 *    wbudowany; u edytora domyślnego = bieżący domyślny albo wbudowany jako start),
 *  - `onSave` — dokąd trafia zapis (osobisty PUT vs admin default PUT),
 *  - `headerContent` — lewa strona nagłówka (powitanie / tytuł ekranu); przyciski edycji dokłada
 *    edytor po prawej.
 *
 * Filtr okresu KPI/wykresu jest PRZEJŚCIOWY (Map<widgetId, period> w komponencie, nie w układzie) —
 * twardy refresh resetuje do domyślnego okresu kafelka. 1:1 z pierwotnym zachowaniem pulpitu.
 */

import { lazy, Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Download, Pencil, Plus, RotateCcw, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { buildDashboardCsv, downloadCsv } from '@/components/dashboard/export-report';
import type {
  Breakpoint,
  DashboardLayoutDoc,
  DashboardPeriod,
  DashboardWidget,
  WidgetLayoutItem,
  WidgetType,
} from '@/components/dashboard/types';
import {
  appendWidgetToLayouts,
  createCustomWidget,
  createWidget,
  customWidgetSize,
  removeWidgetFromLayouts,
} from '@/components/dashboard/widget-factory';
import { WIDGET_REGISTRY } from '@/components/dashboard/widget-registry';
import { WidgetConfigModal } from '@/components/dashboard/WidgetConfigModal';
import { WidgetPickerModal } from '@/components/dashboard/WidgetPickerModal';
import { Dropdown } from '@/components/Dropdown';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import type { WidgetDefinitionDTO } from '@/lib/widgets/types';

const DashboardGrid = lazy(() =>
  import('@/components/dashboard/DashboardGrid').then((m) => ({ default: m.DashboardGrid })),
);

function GridFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <Spinner />
    </div>
  );
}

export interface DashboardEditorProps {
  /** Układ bazowy (poza edycją renderowany wprost; wejście w edycję klonuje go do draftu). */
  baseLayout: DashboardLayoutDoc;
  /** Zapis draftu. Rzucenie → toast błędu, edycja zostaje otwarta. */
  onSave: (layout: DashboardLayoutDoc) => Promise<void>;
  isSaving: boolean;
  /** Lewa strona nagłówka (powitanie / tytuł ekranu). Przyciski edycji dokłada edytor. */
  headerContent: React.ReactNode;
  /**
   * Pasek narzędzi raportowych w nagłówku (okres kafelków, „Eksport raportu", „Nowy dokument").
   * Włączony na pulpicie osobistym; edytor DOMYŚLNEGO pulpitu tenanta go nie pokazuje.
   */
  showReportTools?: boolean;
  /**
   * „Przywróć domyślny": kasuje własny układ usera. Przycisk widać tylko gdy podano `onReset`
   * i `canReset` (user ma zapisany własny układ); poprzedza go potwierdzenie.
   */
  onReset?: () => Promise<void>;
  canReset?: boolean;
}

/** Wartości listy „okres kafelków": domyślny (każdy kafelek swój) albo jeden okres dla wszystkich. */
type GlobalPeriod = 'default' | DashboardPeriod;

export function DashboardEditor({
  baseLayout,
  onSave,
  isSaving,
  headerContent,
  showReportTools = false,
  onReset,
  canReset = false,
}: DashboardEditorProps) {
  const { t } = useTranslation();
  const { show } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Okresy kafelków — przejściowe, poza układem i query (reset po refreshu).
  const [periods, setPeriods] = useState<Map<string, DashboardPeriod>>(() => new Map());

  // Tryb edycji — `draft` to kopia robocza (null poza edycją).
  const [draft, setDraft] = useState<DashboardLayoutDoc | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [configWidgetId, setConfigWidgetId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const [globalPeriod, setGlobalPeriod] = useState<GlobalPeriod>('default');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const handlePeriodChange = useCallback((widgetId: string, period: DashboardPeriod) => {
    setPeriods((prev) => {
      const next = new Map(prev);
      next.set(widgetId, period);
      return next;
    });
  }, []);

  const editMode = draft !== null;
  const layout = draft ?? baseLayout;

  /** Ustawia jeden okres na wszystkich kafelkach z filtrem okresu (albo przywraca domyślne). */
  function handleGlobalPeriod(value: GlobalPeriod) {
    setGlobalPeriod(value);
    if (value === 'default') {
      setPeriods(new Map());
      return;
    }
    setPeriods(
      new Map(
        layout.widgets
          .filter((w) => WIDGET_REGISTRY[w.type].supportsPeriod)
          .map((w) => [w.id, value] as const),
      ),
    );
  }

  async function handleConfirmReset() {
    if (!onReset) return;
    try {
      await onReset();
      setPeriods(new Map());
      setGlobalPeriod('default');
      show({ variant: 'success', title: t('tenant.dashboard.resetDone') });
    } catch {
      show({ variant: 'error', title: t('tenant.dashboard.resetError') });
    } finally {
      setResetConfirmOpen(false);
    }
  }

  /** Eksport CSV z danych, które kafelki mają już wczytane (to, co widać na ekranie). */
  function handleExportReport() {
    const entries = queryClient
      .getQueryCache()
      .findAll({ queryKey: ['tenant', 'dashboard'] })
      .filter((q) => q.state.data !== undefined)
      .map((q) => ({ queryKey: q.queryKey, data: q.state.data }));
    const csv = buildDashboardCsv(entries, t);
    // Sam nagłówek (jedna linia) = żaden kafelek nie ma jeszcze danych.
    if (!csv.includes('\n')) {
      show({ variant: 'error', title: t('tenant.dashboard.header.exportEmpty') });
      return;
    }
    downloadCsv(`raport-pulpitu-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    show({ variant: 'success', title: t('tenant.dashboard.header.exportDone') });
  }

  function enterEdit() {
    setDraft(structuredClone(baseLayout));
  }

  function cancelEdit() {
    setDraft(null);
    setPickerOpen(false);
    setConfigWidgetId(null);
    setPendingRemove(null);
  }

  async function handleSave() {
    if (!draft) return;
    try {
      await onSave(draft);
      show({ variant: 'success', title: t('tenant.dashboard.editMode.saved') });
      cancelEdit();
    } catch {
      show({ variant: 'error', title: t('tenant.dashboard.editMode.saveError') });
    }
  }

  function handleLayoutChange(layouts: Record<Breakpoint, WidgetLayoutItem[]>) {
    setDraft((d) => (d ? { ...d, layouts } : d));
  }

  function handleAddWidget(
    type: WidgetType,
    definition?: WidgetDefinitionDTO,
    configPatch?: Record<string, unknown>,
  ) {
    setDraft((d) => {
      if (!d) return d;
      // Kafel „custom" z wybranej definicji: config = definitionId + snapshot; rozmiar wg typu
      // wizualnego. Pozostałe typy — domyślny config (+ ewentualny preset) i rozmiar z rejestru.
      const isCustom = type === 'custom' && definition;
      const widget = isCustom ? createCustomWidget(definition) : createWidget(type, configPatch);
      const size = isCustom
        ? customWidgetSize(definition.visualType)
        : WIDGET_REGISTRY[type].defaultSize;
      return {
        ...d,
        widgets: [...d.widgets, widget],
        layouts: appendWidgetToLayouts(d.layouts, widget.id, size),
      };
    });
  }

  function handleConfirmRemove() {
    if (!pendingRemove) return;
    const id = pendingRemove;
    setDraft((d) =>
      d
        ? {
            ...d,
            widgets: d.widgets.filter((w) => w.id !== id),
            layouts: removeWidgetFromLayouts(d.layouts, id),
          }
        : d,
    );
    setPendingRemove(null);
  }

  function handleSaveWidgetConfig(updated: DashboardWidget) {
    setDraft((d) =>
      d ? { ...d, widgets: d.widgets.map((w) => (w.id === updated.id ? updated : w)) } : d,
    );
    setConfigWidgetId(null);
  }

  const configWidget = draft?.widgets.find((w) => w.id === configWidgetId) ?? null;
  const pendingRemoveWidget = draft?.widgets.find((w) => w.id === pendingRemove) ?? null;
  const pendingRemoveTitle = pendingRemoveWidget
    ? (pendingRemoveWidget.config.title ??
      t(WIDGET_REGISTRY[pendingRemoveWidget.type].defaultTitleKey))
    : '';

  return (
    <div className="w-full space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        {headerContent}

        <div className="flex flex-wrap items-center gap-2">
          {showReportTools && !editMode && (
            <>
              <Dropdown
                aria-label={t('tenant.dashboard.header.range')}
                value={globalPeriod}
                onChange={(v) => handleGlobalPeriod(v as GlobalPeriod)}
                options={[
                  { value: 'default', label: t('tenant.dashboard.header.rangeDefault') },
                  ...(['today', 'week', 'month', 'year', 'all'] as const).map((p) => ({
                    value: p,
                    label: t(`tenant.dashboard.period.${p}`),
                  })),
                ]}
                className="w-auto"
              />
              <Button
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleExportReport}
              >
                {t('tenant.dashboard.header.exportReport')}
              </Button>
              <Button
                variant="primary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/sale-invoices/new')}
              >
                {t('tenant.dashboard.header.newDocument')}
              </Button>
            </>
          )}
          {editMode ? (
            <>
              <Button
                variant="secondary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setPickerOpen(true)}
              >
                {t('tenant.dashboard.editMode.add')}
              </Button>
              <Button
                variant="secondary"
                leftIcon={<X className="h-4 w-4" />}
                onClick={cancelEdit}
                disabled={isSaving}
              >
                {t('tenant.dashboard.editMode.cancel')}
              </Button>
              <Button
                variant="primary"
                leftIcon={<Check className="h-4 w-4" />}
                onClick={handleSave}
                loading={isSaving}
              >
                {t('tenant.dashboard.editMode.save')}
              </Button>
            </>
          ) : (
            <>
              {onReset && canReset && (
                <Button
                  variant="ghost"
                  leftIcon={<RotateCcw className="h-4 w-4" />}
                  onClick={() => setResetConfirmOpen(true)}
                >
                  {t('tenant.dashboard.resetLayout')}
                </Button>
              )}
              <Button
                variant="secondary"
                leftIcon={<Pencil className="h-4 w-4" />}
                onClick={enterEdit}
              >
                {t('tenant.dashboard.editMode.enter')}
              </Button>
            </>
          )}
        </div>
      </header>

      <Suspense fallback={<GridFallback />}>
        <DashboardGrid
          layout={layout}
          editMode={editMode}
          periods={periods}
          onPeriodChange={handlePeriodChange}
          onLayoutChange={editMode ? handleLayoutChange : undefined}
          onRemoveWidget={editMode ? (id) => setPendingRemove(id) : undefined}
          onConfigureWidget={editMode ? (id) => setConfigWidgetId(id) : undefined}
        />
      </Suspense>

      <WidgetPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleAddWidget}
      />

      <WidgetConfigModal
        widget={configWidget}
        onClose={() => setConfigWidgetId(null)}
        onSave={handleSaveWidgetConfig}
      />

      <ConfirmDialog
        open={resetConfirmOpen}
        variant="primary"
        title={t('tenant.dashboard.resetConfirmTitle')}
        message={t('tenant.dashboard.resetConfirmMessage')}
        confirmLabel={t('tenant.dashboard.resetConfirmLabel')}
        onConfirm={handleConfirmReset}
        onClose={() => setResetConfirmOpen(false)}
      />

      <ConfirmDialog
        open={pendingRemove !== null}
        variant="danger"
        title={t('tenant.dashboard.confirmRemove.title')}
        message={t('tenant.dashboard.confirmRemove.message', { title: pendingRemoveTitle })}
        confirmLabel={t('common.delete')}
        onConfirm={handleConfirmRemove}
        onClose={() => setPendingRemove(null)}
      />
    </div>
  );
}
